/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLocation } from '@tanstack/react-router';
import { usePrivacy } from '../context/PrivacyContext';
import { trackEvent } from '../utils/analytics';

export const useAnalytics = () => {
  const location = useLocation();
  const { hasConsent } = usePrivacy();

  // Create wrapper functions that check consent
  const createTrackingFunction = <T extends any[]>(trackingFn: (...args: T) => void) => {
    return (...args: T) => {
      if (hasConsent) {
        trackingFn(...args);
      }
    };
  };

  // Navigation & Content Engagement Events
  const trackNavigationClick = createTrackingFunction((menuItem: string) => {
    trackEvent('navigation_click', 'Navigation', {
      menu_item: menuItem,
    });
  });

  const trackBreadcrumbSelect = createTrackingFunction((breadcrumbItem: string) => {
    trackEvent('breadcrumb_select', 'Navigation', {
      breadcrumb_item: breadcrumbItem,
    });
  });

  const trackServiceDetailView = createTrackingFunction((serviceId: string, serviceTitle: string) => {
    const eventParams: Record<string, string> = {
      service_id: serviceId,
    };

    if (serviceTitle) {
      eventParams.service_name = serviceTitle;
    }

    trackEvent('service_detail_view', 'Content Engagement', eventParams);
  });

  const trackFaqExpand = createTrackingFunction((faqId: string, faqTitle: string) => {
    const eventParams: Record<string, string> = {
      faq_id: faqId,
    };

    if (faqTitle) {
      eventParams.faq_title = faqTitle;
    }

    trackEvent('faq_expand', 'Content Engagement', eventParams);
  });

  // Search & Filtering Events
  const trackSiteSearch = createTrackingFunction((searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 3) {
      return;
    }

    trackEvent('site_search', 'Discovery', {
      search_term: searchTerm,
    });
  });

  const trackFilterApply = createTrackingFunction((filterType: string, filterValue?: string) => {
    trackEvent('filter_apply', 'Discovery', {
      filter_type: filterType,
      filter_value: filterValue,
    });
  });

  const trackFilterClear = createTrackingFunction((filterType: string) => {
    trackEvent('filter_clear', 'Discovery', {
      filter_type: filterType,
    });
  });

  const trackSortChange = createTrackingFunction((sortOption: string) => {
    trackEvent('sort_change', 'Discovery', {
      sort_option: sortOption,
    });
  });

  // Conversion & Handover Events
  const trackLoginClick = createTrackingFunction(() => {
    trackEvent('login_click', 'Authentication', {
      login_button: 'click',
      page_path: location.pathname,
    });
  });

  const trackLoginCancel = createTrackingFunction(() => {
    trackEvent('login_cancel', 'Authentication', {
      login_cancel: 'click',
    });
  });

  const trackServiceApplication = createTrackingFunction((serviceId: string) => {
    trackEvent('service_application', 'Conversion', {
      service_id: serviceId,
    });
  });

  const trackServiceApplicationStart = createTrackingFunction((serviceDefinitionCode: string, serviceName: string) => {
    trackEvent('service_application_start', 'Conversion', {
      service_definition_code: serviceDefinitionCode,
      service_name: serviceName,
    });
  });

  const trackHandoverSuccess = createTrackingFunction((serviceId: string) => {
    trackEvent('handover_success', 'Conversion', {
      service_id: serviceId,
    });
  });

  const trackHandoverFailure = createTrackingFunction((errorCode: string) => {
    trackEvent('handover_failure', 'Conversion Error', {
      error_code: errorCode,
    });
  });

  // Feedback & Support Events
  const trackSurveyPrompt = createTrackingFunction((surveyId: string) => {
    trackEvent('survey_prompt', 'Feedback', {
      display_survey: surveyId,
    });
  });

  const trackUserSatisfaction = createTrackingFunction((satisfied: boolean) => {
    trackEvent('user_satisfaction_survey', 'Feedback', {
      user_satisfaction_response: satisfied ? 'yes' : 'no',
    });
  });

  const trackSurveySubmit = createTrackingFunction((responseValues: Record<string, any>) => {
    trackEvent('survey_submit', 'Feedback', {
      response_values: JSON.stringify(responseValues),
    });
  });

  const trackSurveyDecline = createTrackingFunction(() => {
    trackEvent('survey_decline', 'Feedback', {
      decline: 'click',
    });
  });

  const trackReportIssue = createTrackingFunction((issueType: string[]) => {
    trackEvent('report_issue', 'Support', {
      issue_type: issueType,
    });
  });

  const trackContactFormSubmit = createTrackingFunction((formId: string, success: boolean) => {
    trackEvent('contact_form_submit', 'Support', {
      form_id: formId,
      status: success ? 'success' : 'failure',
    });
  });

  // Accessibility & Localization Events
  const trackLanguageToggle = createTrackingFunction((langCode: string) => {
    trackEvent('language_toggle', 'Localization', {
      lang_code: langCode,
    });
  });

  const trackPersistedLanguage = createTrackingFunction(() => {
    trackEvent('persisted_language', 'Localization', {
      session_restore: 'true',
    });
  });

  const trackAccessibilityOption = createTrackingFunction((option: string) => {
    trackEvent('accessibility_option', 'Accessibility', {
      option: option,
    });
  });

  const trackAccessibilityToggle = createTrackingFunction((action: 'open_menu' | 'close_menu') => {
    trackEvent('accessibility_toggle', 'Accessibility', {
      [action]: 'click',
    });
  });

  // Performance & Error Tracking Events
  const trackJsException = createTrackingFunction((errorMessage: string, stackTrace?: string) => {
    trackEvent('js_exception', 'Error', {
      error_message: errorMessage,
      stack_trace: stackTrace,
    });
  });

  const trackApiError = createTrackingFunction((endpoint: string, statusCode: number) => {
    trackEvent('api_error', 'Error', {
      endpoint: endpoint,
      status_code: statusCode,
    });
  });

  const trackSlowPage = createTrackingFunction((loadTime: number) => {
    trackEvent('slow_page', 'Performance', {
      page_path: location.pathname,
      load_time: loadTime,
    });
  });

  // Additional EMAN Events
  const trackFaqView = createTrackingFunction((faqId: string, category: string) => {
    trackEvent('faq_view', 'Content Engagement', {
      faq_id: faqId,
      category: category,
    });
  });

  const trackSearchUsed = createTrackingFunction((searchTerm: string, resultsCount: number) => {
    trackEvent('search_used', 'Discovery', {
      search_term: searchTerm,
      results_count: resultsCount,
    });
  });

  const trackZoneFilterApplied = createTrackingFunction((zoneName: string, serviceCount: number) => {
    trackEvent('zone_filter_applied', 'Discovery', {
      zone_name: zoneName,
      service_count: serviceCount,
    });
  });

  const trackServiceApplyClick = createTrackingFunction((serviceId: string, sourcePage: string) => {
    trackEvent('service_apply_click', 'Conversion', {
      service_id: serviceId,
      source_page: sourcePage,
    });
  });

  const trackChatbotEngaged = createTrackingFunction((entryPoint: string, deviceType: string) => {
    trackEvent('chatbot_engaged', 'Support', {
      entry_point: entryPoint,
      device_type: deviceType,
    });
  });

  const trackFeedbackSubmitted = createTrackingFunction(
    (type: string, page: string, category: string, rating?: number) => {
      trackEvent('feedback_submitted', 'Feedback', {
        type: type,
        page: page,
        category: category,
        rating: rating,
      });
    },
  );

  const trackSocialLinkClick = createTrackingFunction((platformName: string) => {
    trackEvent('social_link_click', 'Social', {
      social_platform_name: platformName,
      page: location.pathname,
    });
  });

  const trackContentShare = createTrackingFunction((platform: string, contentTitle: string) => {
    trackEvent('content_share', 'Social', {
      platform: platform,
      content_title: contentTitle,
    });
  });

  // Enhanced gtag wrapper that checks consent
  const gtagWrapper = (...args: any[]) => {
    if (hasConsent && window.gtag) {
      window.gtag(...args);
    }
  };

  return {
    // Consent status
    hasConsent,

    // Enhanced gtag function
    gtag: gtagWrapper,

    // Navigation & Content
    trackNavigationClick,
    trackBreadcrumbSelect,
    trackServiceDetailView,
    trackFaqExpand,
    trackFaqView,

    // Search & Filtering
    trackSiteSearch,
    trackSearchUsed,
    trackFilterApply,
    trackFilterClear,
    trackSortChange,
    trackZoneFilterApplied,

    // Conversion & Handover
    trackLoginClick,
    trackLoginCancel,
    trackServiceApplication,
    trackServiceApplyClick,
    trackServiceApplicationStart,
    trackHandoverSuccess,
    trackHandoverFailure,

    // Feedback & Support
    trackSurveyPrompt,
    trackUserSatisfaction,
    trackSurveySubmit,
    trackSurveyDecline,
    trackReportIssue,
    trackContactFormSubmit,
    trackChatbotEngaged,
    trackFeedbackSubmitted,

    // Accessibility & Localization
    trackLanguageToggle,
    trackPersistedLanguage,
    trackAccessibilityOption,
    trackAccessibilityToggle,

    // Performance & Error
    trackJsException,
    trackApiError,
    trackSlowPage,

    // Social
    trackSocialLinkClick,
    trackContentShare,
  };
};
